import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwc3Vuc2V0fGVufDB8fHx8MTc4NjQ3ODUzNXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold tracking-tight">Trébol · Álvarez Trucking</div>
              <div className="text-xs text-muted-foreground">Gestión de viajes transfronterizos</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Iniciar sesión</h1>
          <p className="mt-1 mb-6 text-sm text-muted-foreground">Ingresá con tu email y contraseña.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                required
                data-testid="login-email"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-1"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
          {/* P35 (2026-09-23): enlaces visibles a las páginas públicas que declara la app de Intuit. */}
          <div className="mt-6 flex justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/terminos" className="hover:underline" data-testid="login-link-terminos">Términos de uso</Link>
            <Link to="/privacidad" className="hover:underline" data-testid="login-link-privacidad">Privacidad</Link>
          </div>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={HERO} alt="Freight truck" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-heading text-2xl font-bold tracking-tight">
            Un solo lugar para cada load, cada cobro y cada pago.
          </div>
          <div className="mt-2 text-sm text-white/80">Laredo, TX · Nuevo Laredo, MX</div>
        </div>
      </div>
    </div>
  );
}
